<template>
  <div class="country-page page-container">
    <Breadcrumbs :breadcrumbs="breadcrumbs" class="mt-4"/>

    <h1>{{ $t('Hotels in') }} {{ countryData?.country_name || route.params.country }}</h1>
    <p v-if="countryData" class="country-subtitle">
      {{ countryData.hotel_count }} {{ $t('hotels across') }} {{ totalCities }} {{ $t('cities') }}
    </p>

    <div v-if="loading" class="country-loading">
      <ProgressBar :value="50" :show-value="false" />
    </div>

    <template v-else-if="countryData">
      <!-- States grouping (for US and similar) -->
      <template v-if="countryData.states?.length">
        <div v-for="state in countryData.states" :key="state.state_code" class="state-section">
          <h2 class="state-name">{{ state.state_name || state.state_code }}</h2>
          <div class="cities-grid">
            <nuxt-link
              v-for="city in state.cities"
              :key="city.slug"
              :to="localePath(`/hotels/${city.slug}`)"
              class="city-card"
            >
              <span class="city-card__name">{{ city.name }}</span>
              <span class="city-card__count">{{ city.hotel_count }} {{ $t('hotels') }}</span>
            </nuxt-link>
          </div>
        </div>
      </template>

      <!-- Flat city list (non-US or ungrouped) -->
      <div v-if="countryData.cities?.length" class="cities-grid">
        <nuxt-link
          v-for="city in countryData.cities"
          :key="city.slug"
          :to="localePath(`/hotels/${city.slug}`)"
          class="city-card"
        >
          <span class="city-card__name">{{ city.name }}</span>
          <span class="city-card__count">{{ city.hotel_count }} {{ $t('hotels') }}</span>
        </nuxt-link>
      </div>
    </template>

    <div v-else class="country-empty">
      {{ $t('No hotels found for this destination.') }}
    </div>

    <!-- Back to all destinations link for crawlers -->
    <div class="back-link">
      <nuxt-link :to="localePath('/hotels/destinations')">
        {{ $t('Browse all destinations') }}
      </nuxt-link>
    </div>

    <div class="cross-link-section">
      <nuxt-link :to="localePath(`/experiences/destinations/${route.params.country}`)" class="cross-link">
        {{ $t('Experiences in') }} {{ countryData?.country_name }}
      </nuxt-link>
    </div>
  </div>
</template>

<script setup lang="ts">
import ProgressBar from 'primevue/progressbar'
import { useHreflangTags, useBreadcrumbSchema } from '~/composables/useSeo'

const config = useRuntimeConfig()
const { $siteConfig } = useNuxtApp()
const localePath = useLocalePath()
const runtimeConfig = useRuntimeConfig()
const route = useRoute()

const brand = computed(() => $siteConfig?.value?.brand || 'Tripening')
const baseUrl = config.public.baseURL

interface City {
  name: string
  slug: string
  state_code: string | null
  state_name: string | null
  hotel_count: number
}

interface State {
  state_code: string
  state_name: string | null
  cities: City[]
}

interface CountryDetail {
  country_code: string
  country_name: string
  slug: string
  hotel_count: number
  states: State[]
  cities: City[]
}

const { data: _countryData } = await useAsyncData(
  `hotel-dest-${route.params.country}`,
  () => $fetch<CountryDetail>(
    `${baseUrl}/api/product/v1/hotels/destinations/${route.params.country}`
  ).catch(() => null)
)
const countryData = computed(() => _countryData.value ?? null)
const loading = ref(false)

const totalCities = computed(() => {
  if (!countryData.value) return 0
  let count = countryData.value.cities?.length || 0
  for (const state of countryData.value.states || []) {
    count += state.cities?.length || 0
  }
  return count
})

const breadcrumbs = computed(() => [
  { label: 'Home', url: '/' },
  { label: 'Hotels', url: '/hotels' },
  { label: 'Destinations', url: '/hotels/destinations' },
  { label: countryData.value?.country_name || (route.params.country as string) }
])

// SEO
const countryName = computed(() => countryData.value?.country_name || (route.params.country as string))

useHead({
  title: computed(() => `Hotels in ${countryName.value} - Find & Compare | ${brand.value}`),
  link: [{ rel: 'canonical', href: computed(() => `${baseUrl}/hotels/destinations/${route.params.country}`) }],
  meta: [
    { name: 'description', content: computed(() => `Find hotels in ${countryName.value}. Browse ${totalCities.value} cities on ${brand.value}. Best prices guaranteed.`) },
    { property: 'og:title', content: computed(() => `Hotels in ${countryName.value} | ${brand.value}`) },
    { property: 'og:url', content: computed(() => `${baseUrl}/hotels/destinations/${route.params.country}`) },
    { property: 'og:type', content: 'website' },
    { name: 'twitter:card', content: 'summary_large_image' },
  ]
})

useHreflangTags()
useJsonld(() => useBreadcrumbSchema(breadcrumbs.value, runtimeConfig.public.baseURL))

useJsonld(() => {
  if (!countryData.value) return null

  const allCities: City[] = [
    ...(countryData.value.cities || []),
    ...(countryData.value.states || []).flatMap(s => s.cities || []),
  ]

  return {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    'name': `Hotels in ${countryData.value.country_name}`,
    'numberOfItems': allCities.length,
    'itemListElement': allCities.slice(0, 50).map((city, index) => ({
      '@type': 'ListItem',
      'position': index + 1,
      'name': city.name,
      'url': `${baseUrl}/hotels/${city.slug}`
    }))
  }
})

definePageMeta({
  layout: 'search'
})
</script>

<style scoped>
.country-page {
  padding-bottom: 60px;
}

.country-page h1 {
  font-size: 28px;
  font-weight: 700;
  margin-top: 16px;
  margin-bottom: 4px;
}

.country-subtitle {
  font-size: 16px;
  color: var(--dark-grey);
  margin-bottom: 32px;
}

.state-section {
  margin-bottom: 32px;
}

.state-name {
  font-size: 20px;
  font-weight: 600;
  margin-bottom: 12px;
  padding-bottom: 8px;
  border-bottom: 1px solid var(--light-grey-border, #e0e0e0);
}

.cities-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 8px;
}

.city-card {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  border-radius: 8px;
  text-decoration: none;
  color: inherit;
  transition: background-color 0.15s;
}

.city-card:hover {
  background-color: rgba(25, 133, 249, 0.06);
}

.city-card__name {
  font-size: 15px;
  font-weight: 500;
}

.city-card__count {
  font-size: 13px;
  color: var(--dark-grey);
  white-space: nowrap;
}

.back-link {
  margin-top: 40px;
  padding-top: 20px;
  border-top: 1px solid var(--light-grey-border, #e0e0e0);
}

.back-link a {
  color: var(--blue, #1985f9);
  font-weight: 500;
  text-decoration: none;
}

.back-link a:hover {
  text-decoration: underline;
}

.cross-link-section {
  margin-top: 16px;
}

.cross-link {
  color: var(--blue, #1985f9);
  font-weight: 500;
  text-decoration: none;
}

.cross-link:hover {
  text-decoration: underline;
}

.country-loading {
  padding: 40px 0;
}

.country-empty {
  text-align: center;
  padding: 60px 0;
  color: var(--dark-grey);
  font-size: 16px;
}

@media screen and (max-width: 768px) {
  .cities-grid {
    grid-template-columns: 1fr;
  }

  .country-page h1 {
    font-size: 22px;
  }
}
</style>
